import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider } from 'react-hook-form';
// Import Zod resolver and schemas
import { zodResolver } from '@hookform/resolvers/zod';
import { presetSchema, tradeSetSchema, wizardSchemaForCreate, wizardSchemaForEdit } from '../../validation/presetSchemas';

import { presetService } from '../../services/presetService';
import { tradeSetService } from '../../services/tradeSetService';
import { toast } from 'react-toastify'; // Ensure this is present and uncommented

import { Button } from '../common/Button';
import TypeSelection from './steps/TypeSelection';
import BasicInfo from './steps/BasicInfo';
import OrderParams from './steps/OrderParams';
import RiskManagement from './steps/RiskManagement';
import AdvancedOptions from './steps/AdvancedOptions';
import WebhookConfig from './steps/WebhookConfig';
import ReviewConfirm from './steps/ReviewConfirm';
// Make sure all necessary types are imported
import { PresetCreateRequest, TradeSetCreateRequest, PresetType, OrderType, Preset } from '../../types/preset';

// Add props interface for Wizard
interface WizardProps {
  onPresetCreated?: (preset: Preset) => void;
  selectedSymbol?: string;
  isInModal?: boolean;
  editingPreset?: Preset; // Add support for editing existing presets
  initialPresetType?: PresetType; // Optional: preselect preset type (e.g., 'tradeset')
}

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const WizardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const WizardTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const StepIndicatorContainer = styled.div`
  display: flex;
  margin-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const StepIndicator = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background-color: ${({ completed }) => (completed ? '#10b981' : '#e5e7eb')};
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 0;
  }
  
  &:last-child:after {
    display: none;
  }
`;

const StepCircle = styled.div<{ active: boolean; completed: boolean }>`
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  background-color: ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#f3f4f6';
  }};
  color: ${({ active, completed }) => {
    if (completed || active) return '#ffffff';
    return '#6b7280';
  }};
  border: 2px solid ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#e5e7eb';
  }};
  z-index: 1;
  margin-right: 0.5rem;
`;

const StepLabel = styled.div<{ active: boolean; completed: boolean }>`
  font-size: 0.875rem;
  font-weight: ${({ active, completed }) => (active || completed ? '600' : '500')};
  color: ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#6b7280';
  }};
  z-index: 1;
`;

const FormContent = styled.div`
  margin-bottom: 2rem;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

// Base steps definition
const allSteps = [
  { id: 'type', label: 'Type' },
  { id: 'basic', label: 'Basic Info' },
  { id: 'order', label: 'Order' }, // Conditional based on presetType
  { id: 'risk', label: 'Risk' }, // Conditional based on presetType & !isCancel
  { id: 'advanced', label: 'Advanced' }, // Conditional based on presetType & orderType==layered
  { id: 'webhook', label: 'Webhook' },
  { id: 'review', label: 'Review' },
];

// Helper function to check if order type is a cancellation type
const isCancellationOrder = (orderType?: OrderType): boolean => {
  return !!orderType && ['cancel_all', 'cancel_bids', 'cancel_asks'].includes(orderType);
};

// Default values specifically for Preset
const defaultPresetValues: Omit<PresetCreateRequest, 'preset_type'> & { preset_type: 'preset' } = {
  name: '',
  description: '',
  symbol: 'BTC-PERP',
  order_type: 'market',
  side: 'buy',
  amount: 0,
  amount_type: 'quote',
  leverage: 1,
  margin_mode: 'cross',
  limit_price: null,
  limit_price_type: null,
  stop_price: null,
  flags: { reduce_only: false, post_only: false },
  take_profit: { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
  stop_loss: { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
  layered: null,
  webhook_enabled: false,
  webhook_id: '',
  webhook_secret: '',
  allow_override: true,
  preset_type: 'preset',
};

// Default values specifically for TradeSet
const defaultTradeSetValues: Omit<TradeSetCreateRequest, 'preset_type'> & { preset_type: 'tradeset' } = {
  name: '',
  description: '',
  symbol: '', // Require symbol for TradeSet
  presets: [], // Required by schema, empty for creation
  webhook_enabled: false,
  webhook_id: '',
  webhook_secret: '',
  allow_override: true,
  preset_type: 'tradeset',
};

// Add compact step indicator styles
const CompactStepContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  margin-bottom: 1.5rem;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CompactStep = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 16px;
  white-space: nowrap;
  background-color: ${({ active, completed }) => {
    if (completed) return '#10b98120';
    if (active) return '#3b82f620';
    return 'transparent';
  }};
  border: 1px solid ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#374151';
  }};
`;

const CompactStepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#374151';
  }};
`;

const CompactStepLabel = styled.span<{ active: boolean; completed: boolean }>`
  font-size: 0.8rem;
  color: ${({ active, completed }) => {
    if (completed) return '#10b981';
    if (active) return '#3b82f6';
    return '#9ca3af';
  }};
`;

// Add a loading spinner component
const LoadingSpinner = styled.span`
  display: inline-block;
  margin-right: 8px;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// TypeScript helper to create a type-safe submit handler for each form type
type PresetSubmitHandler = (data: PresetCreateRequest) => Promise<void>;
type TradeSetSubmitHandler = (data: TradeSetCreateRequest) => Promise<void>;

/**
 * Wizard Component for Preset Creation
 * 
 * This component handles the multi-step form flow for creating presets or tradeSets.
 * It can be used in two modes:
 * 1. Modal mode (isInModal=true): Displayed as a modal overlay with standard step indicators
 * 2. Inline mode (isInModal=false): Embedded directly in another component with compact step indicators
 * 
 * The component adapts its styling and layout based on the isInModal prop to provide
 * an optimal user experience in either context.
 */
const Wizard: React.FC<WizardProps> = ({ 
  onPresetCreated,
  selectedSymbol,
  isInModal = false,
  editingPreset,
  initialPresetType
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(editingPreset ? 1 : 0); // Skip type selection when editing
  const [isLoading, setIsLoading] = useState(false);
  // State to hold the selected type ('preset' or 'tradeset')
  const [selectedPresetType, setSelectedPresetType] = useState<PresetType>('preset');

  // Initialize type from prop if provided
  React.useEffect(() => {
    if (!editingPreset && initialPresetType) {
      setSelectedPresetType(initialPresetType);
      // Jump to basic info step when type is preselected
      setCurrentStepIndex(1);
    }
  }, [initialPresetType, editingPreset]);

  // === Initialize two useForm hooks ===
  const presetFormMethods = useForm<PresetCreateRequest>({
    defaultValues: editingPreset ? {
      // Convert editing preset to form format
      name: editingPreset.name,
      description: editingPreset.description || '',
      symbol: editingPreset.symbol,
      order_type: editingPreset.order_type,
      side: editingPreset.side,
      amount: editingPreset.amount,
      amount_type: editingPreset.amount_type,
      leverage: editingPreset.leverage || 1,
      margin_mode: editingPreset.margin_mode || 'cross',
      limit_price: editingPreset.limit_price,
      limit_price_type: editingPreset.limit_price_type,
      stop_price: editingPreset.stop_price,
      flags: editingPreset.flags || { reduce_only: false, post_only: false },
      take_profit: editingPreset.take_profit || { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
      stop_loss: editingPreset.stop_loss || { enabled: false, trigger_price_value: null, price_type: null, limit_price_value: null, limit_price_type: null },
      layered: editingPreset.layered,
      webhook_enabled: editingPreset.webhook_enabled || false,
      webhook_id: editingPreset.webhook_id || '',
      webhook_secret: '', // Not available in Preset interface, use default
      allow_override: true, // Not available in Preset interface, use default
      preset_type: 'preset' as const,
    } : {
      ...defaultPresetValues,
      symbol: selectedSymbol || defaultPresetValues.symbol // Use provided symbol if available
    },
    mode: 'onChange', // Or 'onBlur' etc.
    resolver: zodResolver(editingPreset ? wizardSchemaForEdit : wizardSchemaForCreate) as any,
  });

  const tradeSetFormMethods = useForm<TradeSetCreateRequest>({
    defaultValues: {
      ...defaultTradeSetValues,
      symbol: selectedSymbol || '' // Use provided symbol if available
    },
    mode: 'onChange',
    resolver: zodResolver(tradeSetSchema) as any,
  });

  // Watch values needed for conditional steps/logic
  const presetOrderType = presetFormMethods.watch('order_type');
  const presetReduceOnly = presetFormMethods.watch('flags.reduce_only'); // Specifically watch reduce_only

  // Watch webhook_enabled specifically based on the active form type
  const webhookEnabled = selectedPresetType === 'preset' 
      ? presetFormMethods.watch('webhook_enabled') 
      : tradeSetFormMethods.watch('webhook_enabled');

  // Use the specifically watched presetOrderType here
  const isCancelType = useMemo(() => isCancellationOrder(presetOrderType), [presetOrderType]);

   // Calculate visible steps dynamically based on selectedPresetType
  const visibleSteps = useMemo(() => {
    const steps = allSteps.filter(step => {
       if (selectedPresetType === 'tradeset') {
         return ['type', 'basic', 'webhook', 'review'].includes(step.id);
       } else { // selectedPresetType === 'preset'
         if (['type', 'basic', 'order', 'webhook', 'review'].includes(step.id)) {
           return true;
         }
         if (step.id === 'risk') {
           const isMarketReduceOnly = presetOrderType === 'market' && presetReduceOnly;
           const isLimitReduceOnly = presetOrderType === 'limit' && presetReduceOnly;
           return !isCancelType && !isMarketReduceOnly && !isLimitReduceOnly;
         }
         if (step.id === 'advanced') {
           const isMarketReduceOnly = presetOrderType === 'market' && presetReduceOnly;
           const isLimitReduceOnly = presetOrderType === 'limit' && presetReduceOnly;
           // Only show advanced if not (market-reduce-only or limit-reduce-only) AND it's a layered order
           return !(isMarketReduceOnly || isLimitReduceOnly) && presetOrderType === 'layered'; 
         }
         return false;
       }
     });
     return steps;
   }, [selectedPresetType, presetOrderType, isCancelType, presetReduceOnly]); // Dependencies based on selected type

  const currentStepId = visibleSteps[currentStepIndex]?.id;

  const handleTypeChange = (type: PresetType) => {
    // Ensure we get a type-safe value
    setSelectedPresetType(type);
    
    // Reset the form when type changes
    if (type === 'preset') {
      tradeSetFormMethods.reset(defaultTradeSetValues);
    } else {
      presetFormMethods.reset(defaultPresetValues);
    }
    
    // Reset to first step when type changes
    setCurrentStepIndex(0);
  };

  const handleNext = async () => {
    console.log('🔍 handleNext called, currentStepIndex:', currentStepIndex, 'visibleSteps length:', visibleSteps.length);
    console.log('🔍 currentStepId:', currentStepId);
    console.log('🔍 visibleSteps:', visibleSteps.map(s => s.id));
    console.log('🔍 isOnFinalStep:', currentStepIndex >= visibleSteps.length - 1);
    
    // If we're not on the last step, validate the current step before proceeding
    if (currentStepIndex < visibleSteps.length - 1) {
      // Determine which fields to validate based on current step
      const currentStepId = visibleSteps[currentStepIndex].id;
      let fieldsToValidate: string[] = [];
      
      // Define fields to validate for each step
      switch (currentStepId) {
        case 'basic':
          fieldsToValidate = ['name', 'symbol', 'description'];
          break;
        case 'order':
          fieldsToValidate = ['order_type', 'side', 'amount', 'amount_type', 'leverage', 'margin_mode'];
          // Add conditional fields based on order type
          if (presetOrderType === 'limit' || presetOrderType === 'stop_limit') {
            fieldsToValidate.push('limit_price', 'limit_price_type');
          }
          if (presetOrderType === 'stop_market' || presetOrderType === 'stop_limit') {
            fieldsToValidate.push('stop_price');
          }
          break;
        case 'risk':
          fieldsToValidate = ['take_profit', 'stop_loss'];
          break;
        case 'advanced':
          // Validate layered order settings when on advanced step
          if (presetOrderType === 'layered') {
            fieldsToValidate = ['layered'];
          } else {
            fieldsToValidate = []; // No validation needed for non-layered orders
          }
          break;
        case 'webhook':
          if (webhookEnabled) {
            fieldsToValidate = ['webhook_id'];
          } else {
            fieldsToValidate = []; // No validation needed when webhook is disabled
          }
          break;
        // Other steps can be added as needed
      }
      
      console.log('🔍 Fields to validate:', fieldsToValidate);
      console.log('🔍 Selected preset type:', selectedPresetType);
      
      // Get current form values to debug
      const currentValues = selectedPresetType === 'preset' 
        ? presetFormMethods.getValues() 
        : tradeSetFormMethods.getValues();
      console.log('🔍 Current form values:', currentValues);
      
      // Trigger validation for the specific fields of the current step
      const isValid = await (selectedPresetType === 'preset' 
          ? presetFormMethods.trigger(fieldsToValidate as any) 
          : tradeSetFormMethods.trigger(fieldsToValidate as any));
      
      console.log('🔍 Validation result:', isValid);
      
      // Get current form errors to debug
      const currentErrors = selectedPresetType === 'preset' 
        ? presetFormMethods.formState.errors 
        : tradeSetFormMethods.formState.errors;
      console.log('🔍 Current form errors:', currentErrors);
      
      if (isValid) {
        console.log('✅ Validation passed, moving to next step');
        setCurrentStepIndex(currentStepIndex + 1);
        // Optionally show success toast when moving between important steps
        if (currentStepId === 'order' || currentStepId === 'risk') {
          console.log('🎉 Showing success toast for', currentStepId);
          toast.success(`${currentStepId === 'order' ? 'Order' : 'Risk'} parameters saved`);
        }
      } else {
        console.log('❌ Validation failed, showing error toast');
        // Show error toast if validation fails
        toast.error(`Please fix validation errors to proceed`);
      }
    } else {
      console.log('🔍 On final step, button should be submit type, not calling handleNext');
    }
    // Note: When on the final step, the "Create PreSet" button is type="submit" 
    // and will naturally trigger the form's onSubmit handler, so we don't need 
    // to handle submission here in handleNext.
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const handleDeletePreset = async (presetId: string) => {
    try {
      await presetService.deletePreset(presetId);
      toast.success('PreSet deleted successfully!');
      
      // Small delay to ensure delete operation is fully processed
      // before triggering any navigation or refresh operations
      setTimeout(() => {
        if (onPresetCreated) {
          // Pass null to indicate deletion - parent should handle this differently
          onPresetCreated(null as any);
        }
      }, 100); // 100ms delay should be sufficient
      
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast.error('Failed to delete preset. Please try again.');
    }
  };

  // Preset-specific submit handler
  const onSubmitPreset: PresetSubmitHandler = async (data) => {
    console.log('🔍 onSubmitPreset called with data:', data);
    console.log('🔍 Take Profit data:', data.take_profit);
    console.log('🔍 Stop Loss data:', data.stop_loss);
    console.log('🔍 Flags data:', data.flags);
    console.log('🔍 API Base URL:', import.meta.env.VITE_API_URL);
    
    // Clean up the data before sending - remove null enum fields when TP/SL is disabled
    const cleanedData = { ...data };
    
    // Explicitly delete the preset_type field as it's not part of the backend schema
    if ('preset_type' in cleanedData) {
      delete (cleanedData as any).preset_type;
    }
    
    if (cleanedData.take_profit && !cleanedData.take_profit.enabled) {
      // Remove enum fields that can't be null when TP is disabled
      if (cleanedData.take_profit.price_type === null) {
        delete (cleanedData.take_profit as any).price_type;
      }
      if (cleanedData.take_profit.limit_price_type === null) {
        delete (cleanedData.take_profit as any).limit_price_type;
      }
    }
    
    if (cleanedData.stop_loss && !cleanedData.stop_loss.enabled) {
      // Remove enum fields that can't be null when SL is disabled
      if (cleanedData.stop_loss.price_type === null) {
        delete (cleanedData.stop_loss as any).price_type;
      }
      if (cleanedData.stop_loss.limit_price_type === null) {
        delete (cleanedData.stop_loss as any).limit_price_type;
      }
    }
    
    // Clean up webhook fields when webhook is disabled
    if (!cleanedData.webhook_enabled) {
      cleanedData.webhook_id = '';
      cleanedData.webhook_secret = '';
    }
    
    setIsLoading(true);
    try {
      let response: Preset;
      if (editingPreset) {
        console.log('🔍 Calling presetService.updatePreset...');
        console.log('🔍 Full payload being sent:', JSON.stringify(cleanedData, null, 2));
        response = await presetService.updatePreset(editingPreset.id, cleanedData);
        console.log('🔍 Preset update successful:', response);
      } else {
        console.log('🔍 Calling presetService.createPreset...');
        console.log('🔍 Full payload being sent:', JSON.stringify(cleanedData, null, 2));
        response = await presetService.createPreset(cleanedData);
        console.log('🔍 Preset creation successful:', response);
      }
      
      console.log('🎉 Showing success toast...');
      toast.success(`PreSet "${data.name}" ${editingPreset ? 'updated' : 'created'} successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Add specific success details
      if (data.webhook_enabled) {
        console.log('🎉 Showing webhook info toast...');
        toast.info(`Webhook enabled with ID: ${data.webhook_id}. Use this ID in your TradingView alerts.`, {
          autoClose: 8000,
        });
      }
      
      // If onPresetCreated callback is provided, call it with the new preset
      if (onPresetCreated && response) {
        console.log('🔍 Calling onPresetCreated callback...');
        onPresetCreated(response as Preset);
      } else if (!isInModal) {
        console.log('🔍 Not in modal mode, could navigate...');
        // Only navigate if not in modal mode
        // navigate('/presets');
      }
    } catch (error: any) {
      console.error('❌ Error creating preset:', error);
      console.log('❌ Error response data:', error.response?.data);
      console.log('❌ Error response status:', error.response?.status);
      console.log('❌ Error response headers:', error.response?.headers);
      
      // Enhanced error handling with more specific messages
      if (error.response?.status === 400) {
        console.log('❌ Showing 400 error toast...');
        toast.error(`Validation error: ${error.message || 'Please check your inputs'}`, {
          autoClose: 7000,
        });
      } else if (error.response?.status === 401) {
        console.log('❌ Showing 401 error toast...');
        toast.error('Authentication error. Please log in again.');
      } else if (error.response?.status === 422) {
        console.log('❌ Showing 422 error toast...');
        const errorDetail = error.response?.data?.detail || 'Validation failed';
        toast.error(`Validation error: ${errorDetail}`, {
          autoClose: 10000,
        });
      } else if (error.response?.status === 500) {
        console.log('❌ Showing 500 error toast...');
        toast.error('Server error. Please try again later or contact support.');
      } else {
        console.log('❌ Showing generic error toast...');
        toast.error(`Failed to create preset: ${error.message || 'Unknown error'}`);
      }
    } finally {
      console.log('🔍 Setting loading to false...');
      setIsLoading(false);
    }
  };

  // TradeSet-specific submit handler
  const onSubmitTradeSet: TradeSetSubmitHandler = async (data) => {
    setIsLoading(true);
    try {
      const response = await tradeSetService.createTradeSet(data);
      toast.success(`TradeSet "${data.name}" created successfully!`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Add specific success details
      if (data.webhook_enabled) {
        toast.info(`Webhook enabled with ID: ${data.webhook_id}. Use this ID in your TradingView alerts.`, {
          autoClose: 8000,
        });
      }
      
      // Similar logic for navigation/callbacks
      if (onPresetCreated && response) {
        onPresetCreated(response as any);
      } else if (!isInModal) {
        // Only navigate if not in modal mode
        // navigate('/tradesets');
      }
    } catch (error: any) {
      console.error('Error creating tradeSet:', error);
      
      // Enhanced error handling with more specific messages
      if (error.response?.status === 400) {
        toast.error(`Validation error: ${error.message || 'Please check your inputs'}`, {
          autoClose: 7000,
        });
      } else if (error.response?.status === 401) {
        toast.error('Authentication error. Please log in again.');
      } else if (error.response?.status === 500) {
        toast.error('Server error. Please try again later or contact support.');
      } else {
        toast.error(`Failed to create tradeSet: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Render the correct form step based on the current index
  const renderStepContent = () => {
     switch (currentStepId) {
       case 'type':
         // Pass the handler down, ensure TypeSelection uses it
         return <TypeSelection onTypeSelect={handleTypeChange} currentType={selectedPresetType} />;
       case 'basic':
         // Pass presetType prop
         return <BasicInfo presetType={selectedPresetType} editingPresetId={editingPreset?.id} onDeletePreset={handleDeletePreset} />;
       case 'order':
         return <OrderParams />;
       case 'risk':
          return <RiskManagement />;
       case 'advanced':
         return <AdvancedOptions />;
       case 'webhook':
         // Pass presetType prop
         return <WebhookConfig presetType={selectedPresetType} hasExistingSecret={!!(editingPreset?.webhook_enabled && editingPreset?.webhook_id)} />;
       case 'review':
         // Pass presetType, NO LONGER passing getFormData
         return <ReviewConfirm presetType={selectedPresetType} />;
       default:
         return <div>Unknown step: {currentStepId}</div>;
     }
   };

  // Render step indicators based on modal/inline mode
  const renderStepIndicators = () => {
    if (isInModal) {
      // Standard step indicators for modal
      return (
        <StepIndicatorContainer>
          {visibleSteps.map((step, index) => {
            const isActive = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;
            
            return (
              <StepIndicator key={step.id} active={isActive} completed={isCompleted}>
                <StepCircle active={isActive} completed={isCompleted}>
                  {isCompleted ? '✓' : index + 1}
                </StepCircle>
                <StepLabel active={isActive} completed={isCompleted}>
                  {step.label}
                </StepLabel>
              </StepIndicator>
            );
          })}
        </StepIndicatorContainer>
      );
    } else {
      // Compact step indicators for inline view
      return (
        <CompactStepContainer>
          {visibleSteps.map((step, index) => {
            const isActive = index === currentStepIndex;
            const isCompleted = index < currentStepIndex;
            
            return (
              <CompactStep key={step.id} active={isActive} completed={isCompleted}>
                <CompactStepDot active={isActive} completed={isCompleted} />
                <CompactStepLabel active={isActive} completed={isCompleted}>
                  {step.label}
                </CompactStepLabel>
              </CompactStep>
            );
          })}
        </CompactStepContainer>
      );
    }
  };

  // Helper function to get button text
  const getButtonText = () => {
    if (isLoading) {
      return editingPreset ? 'Updating...' : 'Creating...';
    }
    
    return `${editingPreset ? 'Update' : 'Create'} ${selectedPresetType === 'preset' ? 'PreSet' : 'TradeSet'}`;
  };

  return (
    <WizardContainer>
      <WizardHeader>
        <WizardTitle>{editingPreset ? 'Edit' : 'Create'} {selectedPresetType === 'preset' ? 'preSet' : 'tradeSet'}</WizardTitle>
      </WizardHeader>
      
      {renderStepIndicators()}
      
      {selectedPresetType === 'preset' ? (
        <FormProvider {...presetFormMethods}>
          <form onSubmit={presetFormMethods.handleSubmit((data) => {
            console.log('🔍 Form onSubmit triggered for preset!');
            onSubmitPreset(data as PresetCreateRequest);
          })}>
            <FormContent>
              {renderStepContent()}
            </FormContent>
            
            <FormActions>
              <Button type="button" onClick={handleBack} disabled={currentStepIndex === 0}>
                Back
              </Button>
              {currentStepIndex < visibleSteps.length - 1 ? (
                <Button type="button" onClick={handleNext} variant="primary">
                  Next
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={isLoading}
                  onClick={(e) => {
                    // Prevent multiple submissions
                    if (isLoading) {
                      e.preventDefault();
                      console.log('🔍 Preventing duplicate submission - already loading');
                      return;
                    }
                    
                    console.log('🔍 Submit button clicked!');
                    console.log('🔍 Form valid:', presetFormMethods.formState.isValid);
                    console.log('🔍 Form errors:', presetFormMethods.formState.errors);
                    console.log('🔍 Current step index:', currentStepIndex);
                    console.log('🔍 Visible steps length:', visibleSteps.length);
                  }}
                >
                  {isLoading && <LoadingSpinner />}
                  {getButtonText()}
                </Button>
              )}
            </FormActions>
          </form>
        </FormProvider>
      ) : (
        <FormProvider {...tradeSetFormMethods}>
          <form onSubmit={tradeSetFormMethods.handleSubmit((data) => {
            console.log('🔍 Form onSubmit triggered for tradeset!');
            onSubmitTradeSet(data as TradeSetCreateRequest);
          })}>
            <FormContent>
              {renderStepContent()}
            </FormContent>
            
            <FormActions>
              <Button type="button" onClick={handleBack} disabled={currentStepIndex === 0}>
                Back
              </Button>
              {currentStepIndex < visibleSteps.length - 1 ? (
                <Button type="button" onClick={handleNext} variant="primary">
                  Next
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={isLoading}
                  onClick={(e) => {
                    // Prevent multiple submissions
                    if (isLoading) {
                      e.preventDefault();
                      console.log('🔍 Preventing duplicate submission - already loading');
                      return;
                    }
                    
                    console.log('🔍 Submit button clicked for tradeset!');
                    console.log('🔍 Form valid:', tradeSetFormMethods.formState.isValid);
                    console.log('🔍 Form errors:', tradeSetFormMethods.formState.errors);
                  }}
                >
                  {isLoading && <LoadingSpinner />}
                  {getButtonText()}
                </Button>
              )}
            </FormActions>
          </form>
        </FormProvider>
      )}
    </WizardContainer>
  );
};

export default Wizard; 